import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useApi } from '../lib/useApi'
import { pushToast } from '../lib/toast.jsx'
import { THEMES } from '../components/Themes/themes.js'
import { applyTheme } from '../lib/useTheme.js'
import { FEATURES, FEATURE_GROUPS, DEFAULT_ENABLED } from '../lib/features.js'

export default function OnboardingPage() {
  const nav = useNavigate()
  const api = useApi()
  const qc  = useQueryClient()
  const [step, setStep] = useState(0)
  const [theme, setTheme] = useState('hunter')
  const [features, setFeatures] = useState(new Set(DEFAULT_ENABLED))
  const [wakeGoal, setWakeGoal] = useState('06:30')
  const [proteinGoal, setProteinGoal] = useState('')
  const [busy, setBusy] = useState(false)

  async function finish() {
    setBusy(true)
    try {
      const updated = await api.patch('/profile', {
        theme,
        onboarded: true,
        enabled_features: [...features],
        goals: {
          wake_time: wakeGoal,
          ...(proteinGoal ? { protein_g: Number(proteinGoal) } : {}),
        },
      })
      // If the request only got queued offline, the server still thinks
      // onboarded=false — navigating to /health would bounce straight back
      // here and loop forever. Surface the failure instead.
      if (!updated || updated._queued) {
        pushToast({ kind: 'error', title: 'Speichern fehlgeschlagen', body: 'Bitte erneut versuchen.' })
        return
      }
      qc.setQueryData(['profile'], updated)
      await qc.invalidateQueries({ queryKey: ['profile'] })
      nav('/health', { replace: true })
    } catch (e) {
      pushToast({ kind: 'error', title: 'Speichern fehlgeschlagen', body: String(e?.message || e).slice(0, 200) })
    } finally {
      setBusy(false)
    }
  }

  function toggleFeature(id) {
    const next = new Set(features)
    next.has(id) ? next.delete(id) : next.add(id)
    setFeatures(next)
  }

  const byGroup = FEATURES.reduce((acc, f) => {
    (acc[f.group] ||= []).push(f)
    return acc
  }, {})

  const TOTAL_STEPS = 4

  return (
    <div className="min-h-screen grid place-items-center px-6 py-8">
      <div className="w-full max-w-2xl space-y-6 border border-white/10 rounded-lg p-6 animate-system-open">
        <div className="section-h">Setup · {step + 1}/{TOTAL_STEPS}</div>

        {step === 0 && (
          <>
            <p className="opacity-80">Pick a design.</p>
            <div className="grid grid-cols-2 gap-3">
              {Object.values(THEMES).map((t) => (
                <button
                  key={t.id}
                  onClick={() => { setTheme(t.id); applyTheme(t.id) }}
                  className={`p-3 rounded-themed border tap text-left ${theme === t.id ? 'border-[var(--accent)]' : 'border-card'}`}
                  style={{
                    background:  t.vars['--card-bg'],
                    borderColor: theme === t.id ? t.vars['--accent'] : t.vars['--card-border'],
                  }}
                >
                  <div className="font-display text-sm" style={{ color: t.vars['--accent'], fontFamily: t.vars['--font-display'] }}>
                    {t.label}
                  </div>
                  <div className="text-[10px] opacity-60 mt-1" style={{ color: t.vars['--muted'] }}>{t.tagline}</div>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <p className="opacity-80">Wähle deine Module — kannst du jederzeit später ändern.</p>
            <div className="text-xs opacity-60 num">
              <b className="glow">{features.size}</b>/{FEATURES.length} aktiv
            </div>
            <div className="space-y-4 max-h-[55vh] overflow-y-auto no-scrollbar pr-1">
              {Object.entries(byGroup).map(([group, list]) => (
                <div key={group} className="space-y-2">
                  <div className="label-mini">{FEATURE_GROUPS[group] || group}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {list.map((f) => {
                      const on = features.has(f.id)
                      return (
                        <button key={f.id} onClick={() => toggleFeature(f.id)}
                                className={`tap text-left px-3 py-2 rounded border ${
                                  on ? 'border-[var(--accent)] bg-[var(--accent)]/10' : 'border-white/10'
                                }`}>
                          <div className="flex items-center justify-between">
                            <div className="font-display text-sm">{f.label}</div>
                            <span className={`text-xs num ${on ? 'glow' : 'opacity-50'}`}>{on ? 'ON' : 'OFF'}</span>
                          </div>
                          <div className="text-[11px] opacity-60 mt-0.5">{f.description}</div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p className="opacity-80">Wake-time goal</p>
            <input type="time" value={wakeGoal} onChange={(e) => setWakeGoal(e.target.value)}
                   className="w-full bg-transparent border border-white/15 rounded-md px-3 py-2 num" />
          </>
        )}

        {step === 3 && (
          <>
            <p className="opacity-80">Protein goal (optional, g/Tag)</p>
            <input type="number" value={proteinGoal} onChange={(e) => setProteinGoal(e.target.value)}
                   placeholder="180" className="w-full bg-transparent border border-white/15 rounded-md px-3 py-2 num" />
          </>
        )}

        <div className="flex justify-between gap-3">
          <button onClick={() => nav('/health', { replace: true })}
                  className="text-xs opacity-60 hover:text-[var(--accent)] label-mini">Skip</button>
          <div className="flex gap-2">
            {step > 0 && (
              <button onClick={() => setStep((s) => s - 1)} className="px-4 py-2 rounded-md border border-white/15 tap">Back</button>
            )}
            {step < TOTAL_STEPS - 1 ? (
              <button onClick={() => setStep((s) => s + 1)} className="px-4 py-2 rounded-md bg-[var(--accent)] text-black font-semibold tap">Next</button>
            ) : (
              <button disabled={busy} onClick={finish} className="px-4 py-2 rounded-md bg-[var(--accent)] text-black font-semibold tap">
                {busy ? '…' : 'Start'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
